import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiCheck, 
  FiCalendar, 
  FiMail, 
  FiUsers, 
  FiArrowRight, 
  FiX, 
  FiClock, 
  FiAlertCircle, 
  FiBriefcase,
  FiShield
} from 'react-icons/fi';

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 100 }
  },
};

const importantItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: [1, 1.02, 1],
    transition: { 
      type: 'spring', 
      stiffness: 100,
      scale: { repeat: Infinity, duration: 2, ease: 'easeInOut' }
    },
  },
};

function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [nextWorkshopDate, setNextWorkshopDate] = useState('');

  const getNextSaturdayWorkshop = () => {
    const now = new Date();
    const nextSaturday = new Date();
    const targetDay = 6; // Saturday
    const targetHour = 20; // 8:00 PM (20:00)
    
    let daysToAdd = (targetDay - now.getDay() + 7) % 7;
    
    // If today is Saturday and it's already past 8:00 PM, move to next week
    if (daysToAdd === 0 && now.getHours() >= targetHour) {
      daysToAdd = 7;
    }
    
    nextSaturday.setDate(now.getDate() + daysToAdd);
    nextSaturday.setHours(targetHour, 0, 0, 0);
    return nextSaturday;
  };

  const formatWorkshopDate = (date) => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'Asia/Kolkata' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getTimeUntilWorkshop = (workshopDate) => {
    const now = new Date();
    const timeDiff = workshopDate.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return "Workshop is starting now!";
    }
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m to go!`;
    } else {
      return `${hours}h ${minutes}m to go!`;
    }
  };

  const handleWhatsAppClick = () => {
    window.open('https://chat.whatsapp.com/GWMhCNCoUGhFuBkqFUUMhL', '_blank');
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    const updateWorkshopDate = () => {
      const nextWorkshop = getNextSaturdayWorkshop();
      setNextWorkshopDate(formatWorkshopDate(nextWorkshop));
    };

    updateWorkshopDate();
    const interval = setInterval(updateWorkshopDate, 60000);
    
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 8000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  const nextWorkshop = getNextSaturdayWorkshop();
  const timeUntilWorkshop = getTimeUntilWorkshop(nextWorkshop);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 font-['Inter']">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-800">10XAI Founder Workshop</h1>
            <div className="hidden md:flex items-center text-emerald-600 font-semibold text-sm">
              <FiShield className="mr-1" /> Verified Registration
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Main Congratulations Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-3xl p-8 mb-8 shadow-sm relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex justify-center mb-4">
                <motion.div 
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 150, damping: 15, delay: 0.2 }}
                  className="bg-emerald-500 rounded-full p-4 shadow-lg shadow-emerald-200"
                >
                  <FiCheck className="text-4xl text-white" />
                </motion.div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-extrabold text-emerald-800 mb-2">
                Success! 🎉
              </h1>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                Registration Confirmed (Paid ₹49)
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
                You've successfully secured your spot for the most comprehensive AI workshop for founders. Get ready to reclaim 20+ hours a week! 🚀
              </p>
            </div>
            {/* Decorative background element */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-100/50 rounded-full -mr-16 -mt-16 blur-2xl"></div>
          </div>
        </motion.div>

        {/* Status Confirmation */}
        <motion.div 
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-md p-6 mb-8 border-l-8 border-emerald-500 flex items-center justify-between"
        >
          <div>
            <h3 className="text-lg font-bold text-gray-800">Workshop Pass: Active ✅</h3>
            <p className="text-gray-500 text-sm italic">Access granted for the upcoming live session</p>
          </div>
          <div className="bg-emerald-100 text-emerald-600 p-3 rounded-xl">
            <FiBriefcase className="text-2xl" />
          </div>
        </motion.div>

        {/* Next Steps Section */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-3xl shadow-xl p-8 mb-8 border border-gray-100"
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center flex items-center justify-center">
            <span className="bg-gray-100 px-4 py-1 rounded-full text-sm font-bold text-gray-500 mr-3">REQUIRED</span>
            Complete These Steps
          </h3>

          <div className="space-y-8">
            {/* Step 1 */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4">
              <div className="bg-blue-600 text-white rounded-2xl w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold shadow-lg shadow-blue-100">1</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-800 flex items-center">
                  <FiMail className="mr-2 text-blue-600" /> Check Your Email
                </h4>
                <p className="text-gray-600 mt-2">
                  Your ticket and invoice have been sent to your inbox.
                </p>
                <div className="mt-3 bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <p className="text-blue-800 text-sm font-medium flex items-start">
                    <FiAlertCircle className="mr-2 mt-0.5 flex-shrink-0" />
                    Check <span className="font-bold mx-1">Spam/Promotions</span> if not found in Inbox. Mark as "Important" to receive the meeting link!
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div variants={itemVariants} className="flex items-start space-x-4">
              <div className="bg-purple-600 text-white rounded-2xl w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold shadow-lg shadow-purple-100">2</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-800 flex items-center">
                  <FiCalendar className="mr-2 text-purple-600" /> Save the Schedule
                </h4>
                <div className="mt-3 p-5 bg-purple-50 rounded-2xl border border-purple-100 relative overflow-hidden">
                  <div className="relative z-10">
                    <p className="text-purple-900 font-extrabold text-xl mb-1">
                      📅 {nextWorkshopDate}
                    </p>
                    <p className="text-purple-700 font-bold text-lg">
                      ⏰ 8:00 PM IST (Saturday)
                    </p>
                    <div className="mt-3 inline-flex items-center px-3 py-1 bg-white rounded-full text-purple-600 text-sm font-bold shadow-sm">
                      <FiClock className="mr-1.5" /> {timeUntilWorkshop}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div variants={importantItemVariants} className="flex items-start space-x-4 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border-2 border-emerald-200 shadow-lg shadow-emerald-50">
              <div className="bg-emerald-600 text-white rounded-2xl w-10 h-10 flex-shrink-0 flex items-center justify-center font-bold shadow-lg shadow-emerald-200">3</div>
              <div className="flex-1">
                <h4 className="text-xl font-bold text-gray-800 flex items-center">
                  <FiUsers className="mr-2 text-emerald-600" /> Join the VIP Group
                </h4>
                <p className="text-gray-700 mt-2 font-medium">
                  The <span className="text-emerald-700 font-bold underline">Live Meeting Link</span> and AI Bonus Resources are shared exclusively in the WhatsApp Community.
                </p>
                <motion.button 
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleWhatsAppClick}
                  className="mt-5 bg-emerald-600 text-white px-8 py-4 rounded-2xl font-extrabold flex items-center justify-center space-x-3 shadow-xl hover:bg-emerald-700 transition-all w-full md:w-auto"
                >
                  <FiUsers className="text-xl" />
                  <span>Join WhatsApp Community</span>
                  <FiArrowRight />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="border-t pt-8 mt-12 text-center"
        >
          <p className="text-gray-500 mb-3">
            Need assistance? Contact us at: 
            <a href="mailto:support@yinguru.com" className="text-emerald-600 font-bold hover:underline ml-1">
              support@yinguru.com
            </a>
          </p>
          <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold">
            © 2026 10XAI Founder Workshop • Built for High-Performance Founders
          </p>
        </motion.div>
      </div>

      {/* Persistence Popup */}
      <AnimatePresence>
        {showPopup && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full p-10 relative overflow-hidden"
            >
              <button 
                onClick={closePopup}
                className="absolute top-6 right-6 text-gray-300 hover:text-gray-600 transition-colors"
              >
                <FiX size={28} />
              </button>
              
              <div className="text-center relative z-10">
                <div className="bg-emerald-100 rounded-3xl w-24 h-24 flex items-center justify-center mx-auto mb-8 shadow-inner">
                  <FiUsers className="text-5xl text-emerald-600" />
                </div>
                
                <h3 className="text-3xl font-extrabold text-gray-900 mb-4 leading-tight">
                  Join the Inner Circle! 📢
                </h3>
                
                <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                  Don't miss the <strong>Live Zoom Link</strong>. It is shared only in our community 15 mins before we start on Saturday!
                </p>
                
                <motion.button 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { handleWhatsAppClick(); closePopup(); }}
                  className="bg-emerald-600 text-white font-extrabold text-xl px-10 py-5 rounded-[1.5rem] w-full shadow-2xl shadow-emerald-200 hover:bg-emerald-700 transition-all flex items-center justify-center space-x-3"
                >
                  <span>Get Session Link 🚀</span>
                </motion.button>
                
                <button 
                  onClick={closePopup}
                  className="text-gray-400 hover:text-gray-600 mt-6 text-sm font-bold transition-colors underline"
                >
                  I'll check my email instead
                </button>
              </div>
              
              {/* Decoration */}
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-emerald-50 rounded-full -mr-16 -mb-16 blur-2xl"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;